import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Spinner } from '@/components/ui/spinner';

export type SessionRenewalDialogState =
  | 'checking'
  | 'action_required'
  | 'signing_in'
  | 'failed';

interface SessionExpiredDialogProps {
  open: boolean;
  error: string | null;
  state: SessionRenewalDialogState;
  onSignIn: () => void;
}

function getDialogCopy(state: SessionRenewalDialogState): {
  readonly title: string;
  readonly description: string;
  readonly action: string;
  readonly disabled: boolean;
} {
  switch (state) {
    case 'checking':
      return {
        action: 'Checking session',
        description: 'We are checking whether your secure session can continue automatically.',
        disabled: true,
        title: 'Checking your session',
      };
    case 'signing_in':
      return {
        action: 'Opening sign-in',
        description: 'Complete the secure sign-in prompt to keep working here.',
        disabled: true,
        title: 'Confirm your session',
      };
    case 'failed':
      return {
        action: 'Try again',
        description: 'The secure sign-in did not complete. You can try again without losing this page.',
        disabled: false,
        title: 'Session still needs confirmation',
      };
    case 'action_required':
      return {
        action: 'Continue session',
        description: 'Your secure session needs confirmation before you continue.',
        disabled: false,
        title: 'Continue your session',
      };
  }
}

export function SessionExpiredDialog({
  error,
  open,
  state,
  onSignIn,
}: SessionExpiredDialogProps) {
  const copy = getDialogCopy(state);
  const showSpinner = state === 'checking' || state === 'signing_in';

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {copy.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {state === 'failed' && error !== null ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogAction disabled={copy.disabled} onClick={onSignIn}>
            {showSpinner ? <Spinner data-icon="inline-start" /> : null}
            {copy.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
